import Modal from 'components/common/Modal';
import Service from 'models/ServiceModel';
import React from 'react';
import { Dispatch } from 'redux';
import { connect } from 'react-redux';
import axios from 'helpers/axios';
import config from 'helpers/config';
import actionTypes from 'helpers/actionTypes';
import ServiceAuthorizationStatus from 'models/types/ServiceAuthorizationStatus';
import AuthorizationType from 'models/types/AuthorizationType';
import Price from 'components/common/Price';

interface Props {
    repairOrderId: string;
    services: Service[];

    onClose: (authorizationCreated: boolean) => void;
    dispatch: Dispatch;
}

type ServiceAction = 'authorize' | 'decline' | 'reset';

interface State {
    isLoading: boolean;
    error?: string;

    type: AuthorizationType;
    note: string;
    actionByServiceId: { [serviceId: string]: ServiceAction };
}

const getStatusLabel = (status: ServiceAuthorizationStatus) => {
    switch (status) {
        case ServiceAuthorizationStatus.Authorized:
            return { text: 'Authorized', className: 'text-green-600' };
        case ServiceAuthorizationStatus.Declined:
            return { text: 'Declined', className: 'text-red-600' };
        default:
            return { text: 'Pending', className: 'text-gray-500' };
    }
};

const actionButtons: { action: ServiceAction; text: string; normalClassName: string; selectedClassName: string }[] = [
    { action: 'authorize', text: 'Authorize', normalClassName: 'text-green-500', selectedClassName: 'bg-green-500 border-green-500 text-white' },
    { action: 'decline', text: 'Decline', normalClassName: 'text-red-500', selectedClassName: 'bg-red-500 border-red-500 text-white' },
    { action: 'reset', text: 'Reset', normalClassName: 'text-gray-500', selectedClassName: 'bg-gray-500 border-gray-500 text-white' },
];

class ManualAUthorizationModal extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props);

        this.state = {
            isLoading: false,
            type: AuthorizationType.ManualInPerson,
            note: '',
            actionByServiceId: {},
        };

        this.submit = this.submit.bind(this);
    }

    selectAction(serviceId: string, action: ServiceAction) {
        const { actionByServiceId } = this.state;
        const next = { ...actionByServiceId };
        if (next[serviceId] === action) {
            delete next[serviceId];
        } else {
            next[serviceId] = action;
        }
        this.setState({ actionByServiceId: next });
    }

    submit() {
        const { repairOrderId, dispatch, onClose } = this.props;
        const { type, note, actionByServiceId } = this.state;

        const entries = Object.entries(actionByServiceId);
        if (!entries.length) {
            return;
        }

        this.setState({ isLoading: true, error: undefined });

        axios
            .post(`${config.apiUrl}/authorization`, {
                repairOrderId,
                type,
                authorizedServiceIds: entries.filter(([, action]) => action === 'authorize').map(([id]) => id),
                declinedServiceIds: entries.filter(([, action]) => action === 'decline').map(([id]) => id),
                resetServiceIds: entries.filter(([, action]) => action === 'reset').map(([id]) => id),
                note,
            })
            .then((res) => {
                const { updatedServices } = res.data;
                (updatedServices || []).forEach((service: Service) => {
                    dispatch({
                        type: actionTypes.UPDATE_REPAIR_ORDER_SERVICE_SUCCESS,
                        payload: service,
                    });
                });
                onClose(true);
            })
            .catch(() => {
                this.setState({ isLoading: false, error: 'Could not save this authorization. Please try again.' });
            });
    }

    render() {
        const { services } = this.props;
        const { isLoading, error, note, actionByServiceId, type } = this.state;
        const hasSelection = Object.keys(actionByServiceId).length > 0;

        return (
            <Modal
                headerText={'Manual Authorization'}
                isOpen={true}
                onClose={() => this.props.onClose(false)}
                isLoading={isLoading}
                isBackgroundWhite={true}
                isDeleteHidden={true}
                saveText={'Save'}
                isSaveHidden={false}
                isSaveDisabled={!hasSelection}
                onSave={this.submit}
            >
                {!services.length ? (
                    <div className="text-center text-gray-600 font-semibold py-4">No services on this repair order.</div>
                ) : (
                    services.map((service) => {
                        const statusLabel = getStatusLabel(service.authorizationStatus);
                        const selected = actionByServiceId[service.id];
                        return (
                            <div key={service.id} className="mb-3 pb-3 border-b border-gray-300">
                                <div className="flex items-center">
                                    <div className="flex-1">
                                        <div className="font-bold text-lg">{service.title}</div>
                                        <div className="font-semibold">
                                            <Price price={service.grandTotalCents || 0} isInCents={true} />{' '}
                                            <span className={`text-xs font-semibold ${statusLabel.className}`}>
                                                ({statusLabel.text})
                                            </span>
                                        </div>
                                    </div>
                                </div>
                                <div className="flex mt-2">
                                    {actionButtons.map((button) => (
                                        <button
                                            key={button.action}
                                            onClick={() => this.selectAction(service.id, button.action)}
                                            className={`px-3 py-1 mr-2 text-sm rounded border clickable ${
                                                selected === button.action
                                                    ? button.selectedClassName
                                                    : 'bg-white border-gray-300 hover:bg-gray-100 font-normal' + " " + button.normalClassName
                                            }`}
                                        >
                                            {button.text}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        );
                    })
                )}

                <div className="mt-6">
                    
                    <div className='pb-4 border-b border-gray-300 mb-6'>

                    <label className="font-bold">
                        Method of Authorization:
                    </label>
                    <div className="flex">
                        {authMethods.map((method) => (
                            <button
                            onClick={() => this.setState({ type: method.value })}
                            key={method.value}
                            className={`p-4 ${
                                type === method.value ? 'bg-blue-500 text-white font-semibold' : 'bg-white hover:bg-blue-100'
                            } text-black rounded-lg   w-1/4 border border-blue-600 clickable mr-2`}
                            >
                                {method.text}
                            </button>
                        ))}
                    </div>
                            </div>

                    <label htmlFor="note" className="font-bold">
                        Note
                    </label>
                    <textarea
                        name="note"
                        className="bg-gray-50 rounded-md border w-full px-2 font-medium py-2 focus:ring-1 focus:outline-none focus:border-blue-500"
                        value={note}
                        onChange={(e) => this.setState({ note: e.currentTarget.value })}
                    ></textarea>

                    {!!error && <div className="text-red-600 font-semibold mt-2">{error}</div>}
                </div>
            </Modal>
        );
    }
}

const authMethods = [
    {
        value: AuthorizationType.ManualInPerson,
        text: 'In person',
    },
    {
        value: AuthorizationType.ManualByPhone,
        text: 'By Phone',
    },
    {
        value: AuthorizationType.ManualByText,
        text: 'By SMS',
    },
    {
        value: AuthorizationType.ManualyByEmail,
        text: 'By Email',
    },
];
export default connect()(ManualAUthorizationModal);
